import {
  IClozeExplanation,
  IKeySegment,
  ISegment,
  ISourceKeySegment,
  IExpressionNote,
  EApiLanguageCode,
  EApiVocabularyBookCategory,
  EApiVocabularyBookLevel,
  IAiVocabulary,
  IClassroomCharacterData,
  EApiCharacterType,
  EApiCharacterProblemType,
  EApiSourceChannel,
  EApiStudyMotivation,
  EApiLanguageLevel,
  EApiStudyTimeGoal,
  EApiClozeLevel,
  EApiTest,
  EApiAttendanceType,
  IClassroomGrammarData,
  IClassroomGrammarStageName,
  IClassroomGrammarTopicName,
} from '@datatype/iyagigo/api-type';

export type TClozeLevel = EApiClozeLevel;

export type TTest = EApiTest;

export enum EStudyConfigurationMode {
  // 학습언어
  STUDY_LANGUAGE = 'study-language',
  // 학습개수
  STUDY_ENGLISH_VOCABULARY_COUNT = 'study-english-vocabulary-count',
  STUDY_ENGLISH_GRAMMAR_COUNT = 'study-english-grammar-count',
  STUDY_ENGLISH_TEST_COUNT = 'study-english-test-count',
  STUDY_JAPANESE_VOCABULARY_COUNT = 'study-japanese-vocabulary-count',
  STUDY_JAPANESE_GRAMMAR_COUNT = 'study-japanese-grammar-count',
  STUDY_JAPANESE_TEST_COUNT = 'study-japanese-test-count',
  // 학습시험 및 난이도
  STUDY_JAPANESE_TEST_LEVEL = 'study-japanese-test-level',
  STUDY_JAPANESE_VOCABULARY_LEVEL = 'study-japanese-vocabulary-level',
  STUDY_JAPANESE_GRAMMAR_LEVEL = 'study-japanese-grammar-level',
  STUDY_ENGLISH_TEST_LEVEL = 'study-english-test-level',
  STUDY_ENGLISH_VOCABULARY_LEVEL = 'study-english-vocabulary-level',
  STUDY_ENGLISH_GRAMMAR_LEVEL = 'study-english-grammar-level',
}

export interface IStudyPlanAnswer {
  sourceChannel: EApiSourceChannel | null;
  studyMotivation: EApiStudyMotivation[];
  languageLevel: EApiLanguageLevel | null;
  studyTimeGoal: EApiStudyTimeGoal | null;
}

export type THomeStudyAttendanceCheckKey =
  | 'isCharacterCheck'
  | 'isClassroomExpressionCheck'
  | 'isConversationCheck'
  | 'isGrammarCheck'
  | 'isClozeCheck'
  | 'isVocabularyBookCheck'
  | 'isMiniStoryCheck'
  | 'isAuthenticExpressionCheck';

export interface IStudyRouteStep {
  title: string;
  description: string;
  path: string;
  attendanceCheckKey: THomeStudyAttendanceCheckKey;
  classroomSubMenu?: 'character' | 'expression' | 'conversation' | 'grammar';
  clipSubMenu?: 'mini-story' | 'authentic-expression';
}

// 첫 학습 전에 받는 설문 4스텝
export type TStudyPlanStep =
  | 'account-choice'
  | 'introduction'
  | 'source-channel'
  | 'study-motivation'
  | 'language-level'
  | 'study-time-goal';

export type TIyagigoMainMenu =
  | 'begin'
  | 'home'
  | 'cloze'
  | 'test'
  | 'hub'
  | 'studio'
  | 'classroom'
  | 'clip'
  | 'speaking'
  | 'vocabulary-book';

export type TIyagigoSubMenu = {
  begin: 'gana' | 'today' | null;
  home: 'begin' | null;
  cloze: 'vocabulary' | 'grammar' | 'listening' | 'sentence' | null;
  // problem: 'home' | 'jlpt1' | 'jlpt2-3' | 'jlpt4-5' | 'toeic950' | 'toeic750-850' | 'toeic550-650';
  'vocabulary-book': 'vocabulary-book-study' | 'vocabulary-book-note' | null;
  test: // | 'home' // <- home을 뺼수있는법...
  'problem';
  // hub: 'home' | 'contact' | 'help' | 'account'; //| 'study-configuration';
  hub: 'contact' | 'help' | 'account' | 'subscription' | 'application-metadata' | null; //| 'study-configuration';
  studio: 'statistic' | 'configuration' | 'league' | null; // dashboard?
  // classroom(일본어 교실) = 입문(글자/기본표현/기초문법). 생생표현/간편이야기는 clip으로 분리됨.
  // character = 글자(히라가나·가타카나) 탭. 내부에서 스크립트 토글.
  classroom: 'grammar' | 'character' | 'expression' | 'conversation' | null;
  // clip = 기존 표현 콘텐츠(생생표현 영상 / 간편이야기 대화)
  clip: 'authentic-expression' | 'mini-story' | null;
  // 상단 탭 축. 말하기(과제 고르기) / 기록(지난 대화 목록)
  speaking: 'talk' | 'record' | null;
};

type TIyagigoSubMenuKey = keyof TIyagigoSubMenu | null;

export type IIyagigoMenu = {
  main: TIyagigoSubMenuKey;
  sub: TIyagigoSubMenu[Exclude<TIyagigoSubMenuKey, null>] | null;
} & {
  [K in keyof TIyagigoSubMenu]: K extends Extract<TIyagigoSubMenuKey, K> ? TIyagigoSubMenu[K] : null;
};

export type TSubject =
  | 'jlpt1'
  | 'jlpt2-3'
  | 'jlpt4-5'
  | 'toeic950'
  | 'toeic750-850'
  | 'toeic550-650'
  | 'topik1'
  | 'topik2';

export type TToeicCategory = 'reading' | 'listening';
export type TJlptCategory = 'vocabulary' | 'grammar' | 'reading' | 'listening';

// ############### workbook ###############
export enum EWorkbookStudyType {
  TODAY_STUDY = 'today-study',
  TODAY_REVIEW = 'today-review',
  BOOKMARK_NOTE = 'bookmark-note',
  INCORRECT_NOTE = 'incorrect-note',
}

export enum EWorkbookItemType {
  SINGLE = 'single',
  BUNDLE = 'bundle',
}

// ############### vocabulary book note ###############
export enum EVocabularyBookNoteType {
  BOOKMARK = 'bookmark',
  INCORRECT = 'incorrect',
}

// ############### vocabulary book study ###############
// cloze의 EWorkbookStudyType 미러. EWorkbookItemType은 cloze와 공유.
export enum EVocabularyBookStudyType {
  // 책 진입 학습 (cloze의 TODAY_STUDY 자리)
  BOOK_STUDY = 'book-study',
  // 저장한 단어 노트
  BOOKMARK_NOTE = 'bookmark-note',
  // 틀린 단어 노트
  INCORRECT_NOTE = 'incorrect-note',
  // 재학습(기억 다지기) 큐 SRS
  REVIEW = 'review',
}

// ############### cloze conversation ###############
/**
 * cloze 대화연습(step 2)의 진행 단계.
 *
 * - `playing`: 대화 말풍선이 순차적으로 타이핑되며 노출되는 중.
 * - `problem`: 문제 line(빈칸)에 도달해 사용자가 선택지(choice)를 고르는 중.
 *              `problemIndex`는 `sourceConversation.problem[]` 배열의 몇 번째 문제인지.
 * - `result`: 사용자가 선택지를 제출한 뒤 정답/해설 토스트가 떠있는 중.
 *             토스트가 닫히면 `playing`으로 복귀해 다음 대화가 이어짐.
 * - `done`: 모든 대화와 문제를 끝낸 상태. step 3으로 넘어갈 준비 완료.
 */
export type TClozeConversationPhase =
  | { type: 'conversation-playing' }
  | { type: 'conversation-problem-choice'; problemIndex: number }
  | { type: 'conversation-problem-check'; problemIndex: number }
  | { type: 'conversation-complete' };

// ############### dialog modal ###############
export type TDialogModal =
  | IPlainTextMoalData
  | IClozeExplanationModalData
  | IProblemScoreModalData
  | IProblemExplanationModalData
  | INeedSigninModalData
  | ITodayTestModalData
  | ITodayStudyModalData
  | IRemoveConfirmModalData
  | IWorkbookQuitModalData
  | IStudyConfigurationModalData
  | ISubscriptionStartModalData
  | IAuthCompleteModalData
  | IVocabularyBookStudyConfigurationModalData
  | IStudyPlanModalData
  | IStudyLanguageModalData
  | IGrammarStageSelectModalData
  | IClassroomExpressionGroupSelectModalData
  | IClassroomConversationGroupSelectModalData
  | ISpeakingStageSelectModalData;

// | IConversationDailyLimitModalData;

export interface IPlainTextMoalData {
  type: 'plain-text';
  data: string;
}

// ############### notice modal ###############
export type TNoticeModal = ISubscriptionPlanLaunchModalData | ISpeakingAppNoticeModalData;

// ############### content modal ###############
export type TContentModal =
  | IStudyCelebrationModalData
  | IClozeCompleteModalData
  | IVocabularyBookCompleteModalData
  | ICharacterCompleteModalData
  | IGrammarCompleteModalData
  | IClassroomExpressionCompleteModalData
  | IClassroomConversationCompleteModalData
  | IMiniStoryCompleteModalData
  | IAuthenticExpressionCompleteModalData;

export interface IStudyCelebrationModalData {
  type: 'study-celebration';
  data: { title: string; description: string };
}

export const isStudyCelebrationModalDataType = (data: TContentModal | null): data is IStudyCelebrationModalData => {
  return data !== null && data.type === 'study-celebration';
};

export interface IClozeCompleteModalData {
  type: 'cloze-complete';
  data: { clozeType: TIyagigoSubMenu['cloze']; score: number; totalScore: number };
}

export interface IClozeExplanationModalData {
  type: 'cloze-explanation';
  data: string[];
}

export interface IProblemScoreModalData {
  type: 'problem-score';
  data: { problemType: 'toeic' | 'jlpt'; score: number; totalScore: number };
}

export interface IProblemExplanationModalData {
  type: 'problem-explanation';
  data: {
    passage: string;
    passageAudioLink: string;
    passageTranslation: string;
    questionA: string;
    questionAAudioLink: string;
    questionATranslation: string;
    explanationA: string;
    vocabulary: string[];
    grammar: string[];
    hanja: string[];
  };
}

export interface INeedSigninModalData {
  type: 'need-signin';
  data: string;
}

export interface ITodayTestModalData {
  type: 'today-test';
  data: { test: EApiTest };
}

export interface ITodayStudyModalData {
  type: 'today-study' | 'bookmark-study';
  data: { study: TIyagigoSubMenu['cloze'] };
}

export type TRemoveConfirmDomain = 'cloze' | 'vocabulary-book';

export interface IRemoveConfirmModalData {
  type: 'remove-confirm';
  data: { workbookKey: string; domain: TRemoveConfirmDomain; isIncorrect: boolean };
}

export interface IWorkbookQuitModalData {
  type: 'workbook-quit';
  data: {
    mainMenu: TIyagigoMainMenu;
    subMenu?:
      | TIyagigoSubMenu['cloze']
      | TIyagigoSubMenu['vocabulary-book']
      | TIyagigoSubMenu['test']
      | TIyagigoSubMenu['hub']
      | TIyagigoSubMenu['studio']
      | TIyagigoSubMenu['classroom']
      | TIyagigoSubMenu['clip']
      | TIyagigoSubMenu['speaking'];
    // 명시 시 mainMenu home 대신 이 경로로 이동 (예: cloze-note에서 진입한 경우 'note')
    nextPath?: string;
  };
}

export interface IStudyConfigurationModalData {
  type: 'study-configuration';
  data: { mode: EStudyConfigurationMode };
}

export interface ISubscriptionPlanLaunchModalData {
  type: 'subscription-plan-launch';
  data: null;
}

export interface ISubscriptionStartModalData {
  type: 'subscription-start';
  data: null;
}

// export interface IConversationDailyLimitModalData {
//   type: 'conversation-daily-limit';
// }

export const isPlainTextModalDataType = (data: TDialogModal | null): data is IPlainTextMoalData => {
  return data !== null && data.type === 'plain-text';
};

export const isClozeCompleteModalDataType = (data: TContentModal | null): data is IClozeCompleteModalData => {
  return data !== null && data.type === 'cloze-complete';
};

export const isClozeExplanationModalDataType = (data: TDialogModal | null): data is IClozeExplanationModalData => {
  return data !== null && data.type === 'cloze-explanation';
};

export const isProblemScoreModalDataType = (data: TDialogModal | null): data is IProblemScoreModalData => {
  return data !== null && data.type === 'problem-score';
};

export const isProblemExplanationModalDataType = (data: TDialogModal | null): data is IProblemExplanationModalData => {
  return data !== null && data.type === 'problem-explanation';
};

export const isNeedSigninModalDataType = (data: TDialogModal | null): data is INeedSigninModalData => {
  return data !== null && data.type === 'need-signin';
};

export const isTodayTestModalDataType = (data: TDialogModal | null): data is ITodayTestModalData => {
  return data !== null && data.type === 'today-test';
};

export const isTodayStudyModalDataType = (data: TDialogModal | null): data is ITodayStudyModalData => {
  return data !== null && (data.type === 'today-study' || data.type === 'bookmark-study');
};

export const isRemoveConfirmModalDataType = (data: TDialogModal | null): data is IRemoveConfirmModalData => {
  return data !== null && data.type === 'remove-confirm';
};

export const isWorkbookQuitModalDataType = (data: TDialogModal | null): data is IWorkbookQuitModalData => {
  return data !== null && data.type === 'workbook-quit';
};

export const isStudyConfigurationModalDataType = (data: TDialogModal | null): data is IStudyConfigurationModalData => {
  return data !== null && data.type === 'study-configuration';
};

export const isSubscriptionPlanLaunchModalDataType = (
  data: TNoticeModal | null,
): data is ISubscriptionPlanLaunchModalData => {
  return data !== null && data.type === 'subscription-plan-launch';
};

export interface ISpeakingAppNoticeModalData {
  type: 'speaking-app-notice';
}

export const isSpeakingAppNoticeModalDataType = (data: TNoticeModal | null): data is ISpeakingAppNoticeModalData => {
  return data !== null && data.type === 'speaking-app-notice';
};

export const isSubscriptionStartModalDataType = (data: TDialogModal | null): data is ISubscriptionStartModalData => {
  return data !== null && data.type === 'subscription-start';
};

export interface IAuthCompleteModalData {
  type: 'auth-complete';
  data: { mode: 'signup' | 'signin' | 'remove' };
}

export const isAuthCompleteModalDataType = (data: TDialogModal | null): data is IAuthCompleteModalData => {
  return data !== null && data.type === 'auth-complete';
};

// 단어집 학습 설정 모달 (레벨 선택, 향후 일일 학습량/난이도 등 통합)
export interface IVocabularyBookStudyConfigurationModalData {
  type: 'vocabulary-book-study-configuration';
  data: null;
}

export const isVocabularyBookStudyConfigurationModalDataType = (
  data: TDialogModal | null,
): data is IVocabularyBookStudyConfigurationModalData => {
  return data !== null && data.type === 'vocabulary-book-study-configuration';
};

// 단어집 한 권 완주 모달
export interface IVocabularyBookCompleteModalData {
  type: 'vocabulary-book-complete';
  data: {
    hardCount: number;
    normalCount: number;
    easyCount: number;
  };
}

export const isVocabularyBookCompleteModalDataType = (
  data: TContentModal | null,
): data is IVocabularyBookCompleteModalData => {
  return data !== null && data.type === 'vocabulary-book-complete';
};

// 글자(가나) 행 학습 완료
export interface ICharacterCompleteModalData {
  type: 'character-complete';
  data: {
    groupLabel: string; // 예: 'あ행'
    correctCount: number;
    totalCount: number;
    incorrectCharacters: string[];
    characterOrder: string[];
    characterType: EApiCharacterType;
    nextGroupKey: string | null;
    nextGroupLabel: string | null;
  };
}

export const isCharacterCompleteModalDataType = (data: TContentModal | null): data is ICharacterCompleteModalData => {
  return data !== null && data.type === 'character-complete';
};

// 문법 학습 완료
export interface IGrammarCompleteModalData {
  type: 'grammar-complete';
  data: {
    languageCode?: EApiLanguageCode;
    grammarLabel: string;
    correctCount: number;
    totalCount: number;
    nextGrammar: {
      stageKey: string;
      topicKey: string;
      grammarKey: string;
      label: string;
    } | null;
  };
}

export const isGrammarCompleteModalDataType = (data: TContentModal | null): data is IGrammarCompleteModalData => {
  return data !== null && data.type === 'grammar-complete';
};

// 기본표현 학습 완료
export interface IClassroomExpressionCompleteModalData {
  type: 'classroom-expression-complete';
  data: {
    expressionLabel: string;
    correctCount: number;
    totalCount: number;
    nextExpression: {
      expressionKey: string;
      label: string;
    } | null;
  };
}

export const isClassroomExpressionCompleteModalDataType = (
  data: TContentModal | null,
): data is IClassroomExpressionCompleteModalData => {
  return data !== null && data.type === 'classroom-expression-complete';
};

// 생활회화 학습 완료
export interface IClassroomConversationCompleteModalData {
  type: 'classroom-conversation-complete';
  data: {
    conversationLabel: string;
    correctCount: number;
    totalCount: number;
    nextExpression: {
      conversationKey: string;
      label: string;
    } | null;
  };
}

export const isClassroomConversationCompleteModalDataType = (
  data: TContentModal | null,
): data is IClassroomConversationCompleteModalData => {
  return data !== null && data.type === 'classroom-conversation-complete';
};

// 간편이야기 학습 완료
export interface IMiniStoryCompleteModalData {
  type: 'mini-story-complete';
  data: null;
}

export const isMiniStoryCompleteModalDataType = (data: TContentModal | null): data is IMiniStoryCompleteModalData => {
  return data !== null && data.type === 'mini-story-complete';
};

// 생생표현 학습 완료
export interface IAuthenticExpressionCompleteModalData {
  type: 'authentic-expression-complete';
  data: null;
}

export const isAuthenticExpressionCompleteModalDataType = (
  data: TContentModal | null,
): data is IAuthenticExpressionCompleteModalData => {
  return data !== null && data.type === 'authentic-expression-complete';
};

// 학습 계획 설정 (온보딩 언어 수준 + 학습 시간 목표)
export interface IStudyPlanModalData {
  type: 'study-plan';
  data: null;
}

export const isStudyPlanModalDataType = (data: TDialogModal | null): data is IStudyPlanModalData => {
  return data !== null && data.type === 'study-plan';
};

// 학습언어 선택 (홈 상단 칩 → 영어/일본어 전환)
export interface IStudyLanguageModalData {
  type: 'study-language';
  data: null;
}

export const isStudyLanguageModalDataType = (data: TDialogModal | null): data is IStudyLanguageModalData => {
  return data !== null && data.type === 'study-language';
};

// 기초문법 단계 선택 (study-configuration 옵션 선택 패턴) — 선택 결과는 classroom-slice에 저장
export interface IGrammarStageSelectModalData {
  type: 'grammar-stage-select';
}

export const isGrammarStageSelectModalDataType = (data: TDialogModal | null): data is IGrammarStageSelectModalData => {
  return data !== null && data.type === 'grammar-stage-select';
};

export interface IClassroomExpressionGroupSelectModalData {
  type: 'classroom-expression-group-select';
}

export const isClassroomExpressionGroupSelectModalDataType = (
  data: TDialogModal | null,
): data is IClassroomExpressionGroupSelectModalData => {
  return data !== null && data.type === 'classroom-expression-group-select';
};

export interface IClassroomConversationGroupSelectModalData {
  type: 'classroom-conversation-group-select';
}

export const isClassroomConversationGroupSelectModalDataType = (
  data: TDialogModal | null,
): data is IClassroomConversationGroupSelectModalData => {
  return data !== null && data.type === 'classroom-conversation-group-select';
};

// AI 말하기 주제(stage) 선택
export interface ISpeakingStageSelectModalData {
  type: 'speaking-stage-select';
}

export const isSpeakingStageSelectModalDataType = (
  data: TDialogModal | null,
): data is ISpeakingStageSelectModalData => {
  return data !== null && data.type === 'speaking-stage-select';
};

// export const isConversationDailyLimitModalDataType = (
//   data: TDialogModal | null,
// ): data is IConversationDailyLimitModalData => {
//   return data !== null && data.type === 'conversation-daily-limit';
// };

// ############### notification toast ###############
export type TNotificationToast =
  | IClozeExplanationToastData
  | IKeyExpressionExplanationToastData
  | IApplicationDownloadToastData
  | IVocabularyExplanationToastData
  | IConversationProblemToastData;

// ############### page toast ###############
export type TPageToast = IAuthenticExpressionToastData | IMiniStoryToastData;

// ############### wall toast ###############
export type TWallToast = ISignupWallToastData | ISubscriptionWallToastData;

export interface ISignupWallToastData {
  type: 'signup-wall';
  data: {
    trigger: 'quota' | 'access';
  };
}

export const isSignupWallToastDataType = (data: TWallToast | null): data is ISignupWallToastData => {
  return data !== null && data.type === 'signup-wall';
};

export interface ISubscriptionWallToastData {
  type: 'subscription-wall';
  data: {
    trigger: 'quota' | 'access';
    feature?: 'speaking' | 'cloze';
    quota?: number;
    attendanceType?: EApiAttendanceType;
  };
}

export const isSubscriptionWallToastDataType = (data: TWallToast | null): data is ISubscriptionWallToastData => {
  return data !== null && data.type === 'subscription-wall';
};

export interface IClozeExplanationToastData {
  type: 'cloze-explanation';
  data: {
    clozeType: 'vocabulary' | 'grammar';
    answerExplanation?: IClozeExplanation | null;
    addExplanation?: IClozeExplanation[] | null;
  };
}

export const isClozeExplanationToastDataType = (
  data: TNotificationToast | null,
): data is IClozeExplanationToastData => {
  return data !== null && data.type === 'cloze-explanation';
};

export interface IKeyExpressionExplanationToastData {
  type: 'key-expression-explanation';
  data: {
    mode: 'key' | 'plain';
    sourceKeySegment: ISourceKeySegment | null;
    keySegment: IKeySegment;
    keyExpressionNote: IExpressionNote | null;
    expressionNote: IExpressionNote | null;
  };
}

export const isKeyExpressionExplanationToastDataType = (
  data: TNotificationToast | null,
): data is IKeyExpressionExplanationToastData => {
  return data !== null && data.type === 'key-expression-explanation';
};

export interface IApplicationDownloadToastData {
  type: 'application-download';
  data: null;
}

export const isApplicationDownloadToastDataType = (
  data: TNotificationToast | null,
): data is IApplicationDownloadToastData => {
  return data !== null && data.type === 'application-download';
};

export interface IConversationProblemToastChoice {
  sentence: string;
  pronunciation: string;
  translation: string;
  explanation: string;
  isCorrect: boolean;
  isSelected: boolean;
}

export interface IConversationProblemToastData {
  type: 'conversation-problem';
  data: {
    isCorrect: boolean;
    choices: IConversationProblemToastChoice[];
    problemExplanation: string;
  };
}

export const isConversationProblemToastDataType = (
  data: TNotificationToast | null,
): data is IConversationProblemToastData => {
  return data !== null && data.type === 'conversation-problem';
};

// 단어집 해설 토스트 (한자/유의어/형변화/추가 예문)
export interface IVocabularyExplanationToastData {
  type: 'vocabulary-explanation';
  data: {
    aiVocabulary: IAiVocabulary;
  };
}

export const isVocabularyExplanationToastDataType = (
  data: TNotificationToast | null,
): data is IVocabularyExplanationToastData => {
  return data !== null && data.type === 'vocabulary-explanation';
};

export interface IAuthenticExpressionToastData {
  type: 'authentic-expression';
  data: {
    languageCode: EApiLanguageCode;
    releaseDate: string;
  };
}

export const isAuthenticExpressionToastDataType = (data: TPageToast | null): data is IAuthenticExpressionToastData => {
  return data !== null && data.type === 'authentic-expression';
};

export interface IMiniStoryToastData {
  type: 'mini-story';
  data: {
    languageCode: EApiLanguageCode;
    releaseDate: string;
  };
}

export const isMiniStoryToastDataType = (data: TPageToast | null): data is IMiniStoryToastData => {
  return data !== null && data.type === 'mini-story';
};

// ###################################
// vocabulary-book domain (view 헬퍼)
// — 백엔드 응답 타입/enum은 api-type.ts에 정의됨
// ###################################

// 단어집 라벨 매핑 — 백엔드 EVocabularyBookLevel.description 그대로
export const VOCABULARY_BOOK_LEVEL_LABEL: Record<EApiVocabularyBookLevel, string> = {
  [EApiVocabularyBookLevel.JLPT5]: 'JLPT N5',
  [EApiVocabularyBookLevel.JLPT4]: 'JLPT N4',
  [EApiVocabularyBookLevel.JLPT3]: 'JLPT N3',
  [EApiVocabularyBookLevel.JLPT2]: 'JLPT N2',
  [EApiVocabularyBookLevel.JLPT1]: 'JLPT N1',
  [EApiVocabularyBookLevel.TOEIC550]: 'TOEIC 550',
  [EApiVocabularyBookLevel.TOEIC650]: 'TOEIC 650',
  [EApiVocabularyBookLevel.TOEIC750]: 'TOEIC 750',
  [EApiVocabularyBookLevel.TOEIC850]: 'TOEIC 850',
  [EApiVocabularyBookLevel.TOEIC950]: 'TOEIC 950',
  [EApiVocabularyBookLevel.EASIEST]: '매우 쉬움',
  [EApiVocabularyBookLevel.EASY]: '쉬움',
  [EApiVocabularyBookLevel.NORMAL]: '보통',
  [EApiVocabularyBookLevel.HARD]: '어려움',
  [EApiVocabularyBookLevel.HARDEST]: '매우 어려움',
};

// 카테고리 라벨
export const VOCABULARY_BOOK_CATEGORY_LABEL: Record<EApiVocabularyBookCategory, string> = {
  [EApiVocabularyBookCategory.TEST]: '시험준비',
  [EApiVocabularyBookCategory.STUDY]: '일반학습',
};

// 시험준비(TEST)는 언어별 시험이 달라 분기 필요. 일반학습(STUDY)은 언어 무관 동일.
export const VOCABULARY_BOOK_LEVEL_ORDER: Record<
  EApiVocabularyBookCategory,
  Record<EApiLanguageCode, EApiVocabularyBookLevel[]>
> = {
  [EApiVocabularyBookCategory.TEST]: {
    [EApiLanguageCode.JAPANESE]: [
      EApiVocabularyBookLevel.JLPT5,
      EApiVocabularyBookLevel.JLPT4,
      EApiVocabularyBookLevel.JLPT3,
      EApiVocabularyBookLevel.JLPT2,
      EApiVocabularyBookLevel.JLPT1,
    ],
    [EApiLanguageCode.ENGLISH]: [
      EApiVocabularyBookLevel.TOEIC550,
      EApiVocabularyBookLevel.TOEIC650,
      EApiVocabularyBookLevel.TOEIC750,
      EApiVocabularyBookLevel.TOEIC850,
      EApiVocabularyBookLevel.TOEIC950,
    ],
  },
  [EApiVocabularyBookCategory.STUDY]: {
    [EApiLanguageCode.JAPANESE]: [
      EApiVocabularyBookLevel.EASIEST,
      EApiVocabularyBookLevel.EASY,
      EApiVocabularyBookLevel.NORMAL,
      EApiVocabularyBookLevel.HARD,
      EApiVocabularyBookLevel.HARDEST,
    ],
    [EApiLanguageCode.ENGLISH]: [
      EApiVocabularyBookLevel.EASIEST,
      EApiVocabularyBookLevel.EASY,
      EApiVocabularyBookLevel.NORMAL,
      EApiVocabularyBookLevel.HARD,
      EApiVocabularyBookLevel.HARDEST,
    ],
  },
};

// 한 세션 단어 수 (보고서 권고)
export const VOCABULARY_SESSION_SIZE = 5;

// 품사 라벨 — 백엔드 EWordClass enum name 그대로 매핑
export const VOCABULARY_WORD_CLASS_LABEL: Record<string, string> = {
  NOUN: '명사',
  VERB: '동사',
  ADJECTIVE: 'い형용사',
  ADJECTIVE_VERB: 'な형용사',
  ADVERB: '부사',
  CONJUNCTION: '접속사',
  INTERJECTION: '감탄사',
  ATTRIBUTIVE: '연체사',
  AUXILIARY_VERB: '조동사',
  PARTICLE: '조사',
  PREPOSITION: '전치사',
  PRONOUN: '대명사',
};

// ###################################
// character (일본어교실 입문 — 가나) 프론트 전용 타입
// - 글자종류/청탁음 enum과 응답 타입은 api-type.ts (EApiCharacterType / IClassroomCharacterData)
// ###################################

// 화면용 글자 그룹 (한 행)
export interface ICharacterGroup {
  key: string; // 그룹 키 (예: 'a', 'ka')
  label: string; // 그룹 라벨 (예: 'あ행' — 첫 글자 기반)
  characters: IClassroomCharacterData[];
}

// 미니퀴즈 선택지 (백엔드 problem.choice → 표시 형태로 변환)
export interface ICharacterQuizChoice {
  id: string;
  label: string; // READING이면 한글 발음(ko), LISTENING이면 가나 표기(ja)
  sublabel: string; // 병기용 반대편 표기 — 채점 후 노출
  isCorrect: boolean;
}

// 미니퀴즈 1문제 (백엔드 problem 1개 → 화면용 문제)
export interface ICharacterQuizQuestion {
  type: EApiCharacterProblemType;
  character: string; // 이 문제가 속한 글자. 촉음·요음의 단어 문제도 っ/ゃ/ゅ/ょ로 평가
  question: string; // READING: 화면에 표시할 글자/단어 (LISTENING은 표시 안 함)
  audioLink: string | null; // LISTENING 재생 mp3
  choices: ICharacterQuizChoice[]; // 일반 글자 행 크기만큼(3~5지), 촉음/요음 4지
}

// ###################################
// grammar (일본어교실 입문 — 기초문법) 화면 계층 타입
// - 백엔드의 평탄한 classroom grammar 응답을 단계(stage) > 주제(topic) > 문법(grammar)으로 묶어 사용.
// ###################################

export type IGrammarItem = IClassroomGrammarData;

// 주제 — 중분류
export interface IGrammarTopic {
  key: string;
  order: number;
  name: IClassroomGrammarTopicName;
  grammars: IGrammarItem[];
}

// 단계 — 대분류
export interface IGrammarStage {
  key: string;
  order: number;
  name: IClassroomGrammarStageName;
  topics: IGrammarTopic[];
}
